import type { PureQueryOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as R from 'ramda';
import { useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router';
import { graphql } from '~/apollo/generated/v3';
import type { AdminPagePageQuery } from '~/apollo/generated/v3/graphql';
import { CollapseButton } from '~/components/common/buttons/CollapseButton';
import { SpinnerIcon } from '~/components/common/SpinnerIcon';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { Menu } from '~/components/ui/menu';
import { adminPageRoute, adminPageUpdateRoute } from '~/paths';
import { cn } from '~/utils/common';
import { createRefetchQueries } from '~/utils/graphql';

const ADMIN_PAGE_PAGE = graphql(`
  query AdminPagePage {
    pageList {
      ...pageParts
    }
  }
`);

export type OutletContext = {
  pageList: AdminPagePageQuery['pageList'];
  refetchQueries: PureQueryOptions[];
};

export default function AdminPagePage() {
  useBreadcrumb('routes/admin/page', 'Pages');

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { data, loading } = useQuery(ADMIN_PAGE_PAGE);

  const refetchQueries = createRefetchQueries([{ query: ADMIN_PAGE_PAGE }]);

  const pageList = R.sortBy(p => p.pageName, data?.pageList ?? []);

  const outletContext: OutletContext = { pageList: pageList, refetchQueries };

  if (loading) return <SpinnerIcon />;

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div
          className={cn('w-1/4 space-x-1', {
            'text-center': !isSidebarCollapsed,
          })}
        >
          <CollapseButton
            size="sm"
            collapsed={isSidebarCollapsed}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            Sidebar
          </CollapseButton>

          <NavLink
            to={adminPageRoute()}
            end
            className={({ isActive }) =>
              cn('btn btn-primary btn-sm', { 'btn-disabled': isActive })
            }
          >
            <FontAwesomeIcon icon={faPlus} /> Create Page
          </NavLink>
        </div>
      </div>

      <div className="flex gap-4">
        {!isSidebarCollapsed && (
          <div className="w-1/4">
            <Menu>
              {pageList.map(p => (
                <Menu.Item key={p.id}>
                  <NavLink to={adminPageUpdateRoute(p.id)} className="block">
                    {({ isActive }) => (
                      <>
                        <div className="font-semibold">{p.title}</div>
                        <div
                          className={cn('text-sm', {
                            'text-muted': !isActive,
                          })}
                        >
                          {p.pageName}
                        </div>
                      </>
                    )}
                  </NavLink>
                </Menu.Item>
              ))}
            </Menu>
          </div>
        )}

        <div
          className={cn({
            'w-3/4': !isSidebarCollapsed,
            'w-full': isSidebarCollapsed,
          })}
        >
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
}

export function useUploadPageContext(): OutletContext {
  return useOutletContext<OutletContext>();
}
